import { OrderRefusalDialogComponent } from '../refusal/order-refusal-dialog.component';
import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  FormControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatDrawer } from '@angular/material/sidenav';
import { MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { OrderDefaultsDialogComponent } from './order-defaults-dialog.component';
import { OrderCancelDialogComponent } from './order-cancel-dialog.component';
import { captureOrderDefaults, orderDefaultFormValue } from './order-list-defaults';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { finalize, firstValueFrom, Subscription } from 'rxjs';
import { NewCatalogService } from 'src/app/new-catalog/new-catalog.service';
import { environment } from 'src/environments/environment';
import { ComputerService } from 'src/app/computer/computer.service';
import { ThermalPrintTargetService } from 'src/app/computer/thermal-print-target.service';
import {
  ClientInvoicePrintType,
  ClientInvoiceService
} from 'src/app/docs/client-invoice/client-invoice.service';
import { PaymentLinkDialogComponent } from 'src/app/docs/client-invoice/payment-link-dialog/payment-link-dialog.component';
import {
  ShipmentByInvoicePrintType,
  ShipmentByInvoiceService
} from 'src/app/docs/shipment-by-invoice/shipment-by-invoice.service';
import { AppState } from 'src/app/redux/app.state';
import { extractApiErrorMessage } from 'src/app/shared/utils/extract-api-error-message.util';
import {
  createRequestId,
  resolveStableRequestId,
  StableRequestIdState
} from 'src/app/shared/utils/request-id.util';
import {
  confirmClientDebtWarning,
  getClientDebtWarning,
  getDebtWarningMessages
} from 'src/app/shared/utils/client-debt-warning.util';
import { NotificationService } from 'src/app/services/notification.service';
import { SupportService } from 'src/app/support.service';
import { TerminalPaymentWaitDialogComponent } from 'src/app/terminal-payment/terminal-payment-wait-dialog.component';
import { TerminalProfileSelectDialogComponent } from 'src/app/terminal-payment/terminal-profile-select-dialog.component';
import {
  TerminalPaymentAttempt,
  TerminalPaymentService,
  TerminalProfile
} from 'src/app/terminal-payment/terminal-payment.service';
import { DeviceTypeEnum } from 'src/enums';
import {
  Order,
  OrderDeliveryMethod,
  OrderLight,
  OrderPaymentStatus,
  OrderProcurementStatus,
  OrderFulfillmentStatus,
  OrderShipmentStatus,
  OrderSupplyStatus,
  NovaPoshtaDeliveryKind,
  ProductInDoc,
  User
} from 'src/typings';
import {
  OrderPaymentDialogComponent,
  OrderPaymentDialogResult
} from '../order-payment-dialog/order-payment-dialog.component';
import { OrderInvoiceService } from '../order-invoice.service';
import {
  getOrderPaymentDue,
  getOrderPaymentStatusLabel,
  getOrderShipmentStatusLabel,
  getOrderSupplyStatusLabel
} from '../order-status.util';
import { OrderService, OrderSupplyAllocation, OrderSupplyResponse, SearchOrderDto } from '../order.service';
import { ProductDeliveryHint, productDeliveryHint } from './order-product-delivery.util';
import { orderDeliveryHint } from './delivery-deadline.util';
import { orderLookupMatch } from './order-lookup-match.util';

type ProductImage = { name?: string; url?: string | null };

type OrderProductApiRow = {
  images?: ProductImage[];
  id: number;
  order_product_id?: number;
  description: string;
  brand: string;
  article: string;
  quan: number;
  price: number;
  stock: number;
  provider: string;
  sum: number;
  shipped_quan: number;
  remaining_to_ship_quan: number;
  waiting_supply_quan: number;
  is_ready: boolean;
  returned_quan?: number;
  return_doc_ids?: number[];
};

type ProductRow = {
  images?: ProductImage[];
  orderProductId?: number;
  productId?: number;
  is_ready?: boolean;
  delivery: ProductDeliveryHint;
  quan: number;
  price: number;
  shipped_quan?: number;
  remaining_to_ship_quan?: number;
  waiting_supply_quan?: number;
  returned_quan?: number;
  return_doc_ids?: number[];
  description?: string;
  article?: string;
  brand?: string;
  provider?: string;
  stock?: number;
  priceLabel: string;
  sumLabel: string;
  shippedQuanClass: string;
  remainingToShipClass: string;
  waitingSupplyClass: string;
  hasReturns: boolean;
  returnedQuanLabel?: string;
  returnDocIdsLabel?: string;
};

type OrderRow = Omit<OrderLight, 'products'> & {
  lookupMatch: ReturnType<typeof orderLookupMatch>;
  products: ProductRow[];
  dateLabel: string;
  deliveryDeadline: ReturnType<typeof orderDeliveryHint>;
  supplyStatusLabel: string;
  paymentStatusLabel: string;
  shipmentStatusLabel: string;
  supplyStatusClass: string;
  paymentStatusClass: string;
  shipmentStatusClass: string;
  paidTotalLabel: string;
  sumLabel: string;
  invoiceTotalLabel: string;
  collectibleTotalLabel: string;
  returnedTotalLabel: string;
  refundDueLabel: string;
  paymentDue: number;
  paymentDueLabel: string;
  rowClass: string;
};

type OrderActionKey =
  | 'print_order_thermal'
  | 'print_order_thermal_articles'
  | 'print_invoice_article'
  | 'print_invoice_id'
  | 'print_shipment_article'
  | 'print_shipment_id'
  | 'print_assembly_sheet'
  | 'create_invoice'
  | 'create_parts_selection'
  | 'payment_link'
  | 'terminal_payment'
  | 'open_payments'
  | 'create_payment'
  | 'create_sale'
  | 'create_shipment_by_invoice'
  | 'silent_shipment'
  | 'edit'
  | 'copy'
  | 'cancel'
  | 'delete'
  | 'take_manager'
  | 'request_procurement'
  | 'confirm_procurement';

type PartsSelectionOrderLink = {
  id: number;
  label: string;
};

@Component({
  selector: 'app-order-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('detailExpand', [
      state(
        'collapsed',
        style({ height: '0px', minHeight: '0', opacity: 0 })
      ),
      state('expanded', style({ height: '*', opacity: 1 })),
      transition('collapsed => expanded', animate('180ms cubic-bezier(0.2, 0, 0, 1)')),
      transition('expanded => collapsed', animate('140ms cubic-bezier(0.4, 0, 1, 1)'))
    ])
  ]
})
export class ListComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() context: 'page' | 'client' = 'page';
  @Input() clientId: number | null = null;

  private initialized = false;
  private defaultsOwnerId: number | null = null;
  defaultsDialogOpened = false;

  @HostBinding('class.clientOrders')
  get isClientContext() { return this.context === 'client'; }

  private get scopedClientId(): number | undefined {
    const id = Number(this.clientId);
    return Number.isInteger(id) && id > 0 ? id : undefined;
  }

  @ViewChild('thermalMenuTrigger') thermalMenuTrigger?: MatMenuTrigger;

  @ViewChild('assemblyMenuTrigger') assemblyMenuTrigger?: MatMenuTrigger;

  @ViewChild('cashierSearchInput') cashierSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('managerSearchInput') managerSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('ordersTable', { read: ElementRef }) ordersTable?: ElementRef<HTMLElement>;
  @ViewChild('ordersScroll') ordersScroll?: ElementRef<HTMLElement>;
  @ViewChild('drawer') filterDrawer?: MatDrawer;
  @ViewChild('paymentFilter') paymentFilter?: MatSelect;
  @ViewChild('keyboardHelpTooltip') keyboardHelpTooltip?: MatTooltip;

  keyboardOrderId: number | null = null;
  private initialFocusTimer: ReturnType<typeof setTimeout> | null = null;
  readonly keyboardHelpText = 'Заказы без мышки\n\n' +
    'F2 — поиск «Касса»: телефон или ID, все менеджеры и склады\n' +
    'F3 — поиск «Менеджер»: клиент, авто, VIN или товар\n' +
    'Enter в поиске — найти заказы\n' +
    '↓ из поиска — перейти к первому найденному заказу\n' +
    '↑ / ↓ в списке — предыдущий / следующий заказ\n' +
    'Enter на строке — раскрыть / свернуть заказ\n' +
    'Tab / Shift+Tab — следующее / предыдущее действие\n' +
    'Alt+3 — перейти к фильтрам\n' +
    'Alt+4 — новый заказ\n' +
    'Esc — свернуть раскрытый заказ; иначе — предыдущая страница\n' +
    'Alt+0 — показать / скрыть эту подсказку\n\n' +
    'В открытых окнах, списках и календарях действуют их собственные клавиши.';

  ngAfterViewInit(): void {
    if (this.isClientContext) return;
    this.initialFocusTimer = setTimeout(() => {
      this.initialFocusTimer = null;
      const doc = this.cashierSearchInput?.nativeElement.ownerDocument;
      if (this.destroyed || !doc || this.hasKeyboardOverlay()) return;
      // Do this once, without taking focus back from a field the operator already chose.
      if (doc.activeElement?.matches('input, textarea, select, [contenteditable="true"]')) return;
      this.focusOrderSearch('cashier');
    });
  }

  focusOrderSearch(kind: 'cashier' | 'manager'): void {
    const input = (kind === 'cashier' ? this.cashierSearchInput : this.managerSearchInput)?.nativeElement;
    if (this.destroyed || this.isClientContext || !input || input.disabled) return;
    input.focus({ preventScroll: true });
    input.select();
  }

  private hasKeyboardOverlay(): boolean {
    const doc = this.cashierSearchInput?.nativeElement.ownerDocument;
    return Boolean(this.dialog.openDialogs?.length || (doc &&
      Array.from(doc.querySelectorAll('.cdk-overlay-pane')).some(pane =>
        pane.childElementCount > 0 && !pane.querySelector('.mat-tooltip, .mat-mdc-tooltip'))));
  }

  private async focusOrderFilters(): Promise<void> {
    await this.filterDrawer?.open();
    if (!this.destroyed && !this.isClientContext && !this.hasKeyboardOverlay()) this.paymentFilter?.focus();
  }

  private keyboardRows(): HTMLElement[] {
    return Array.from(this.ordersTable?.nativeElement.querySelectorAll<HTMLElement>('[data-keyboard-order-id]') || []);
  }

  private focusOrderRow(row: HTMLElement | undefined): void {
    if (!row || this.loading) return;
    this.keyboardOrderId = Number(row.getAttribute('data-keyboard-order-id'));
    row.focus({ preventScroll: true });
    row.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    this.cdr.markForCheck();
  }

  @HostListener('document:keydown', ['$event'])
  onOrderListKeydown(event: KeyboardEvent): void {
    if (this.destroyed || this.isClientContext || !this.cashierSearchInput ||
      event.defaultPrevented || event.isComposing || event.getModifierState?.('AltGraph') ||
      this.hasKeyboardOverlay()) return;
    const plain = !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey;
    const altDigit = event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey
      ? /^(?:Digit|Numpad)([034])$/.exec(event.code)?.[1] : undefined;
    if ((plain && (event.key === 'F2' || event.key === 'F3')) || altDigit !== undefined) {
      event.preventDefault();
      if (event.repeat) return;
      if (altDigit === '0') this.keyboardHelpTooltip?.toggle();
      else if (altDigit === '3') void this.focusOrderFilters();
      else if (altDigit === '4') this.toggleFab();
      else this.focusOrderSearch(event.key === 'F2' ? 'cashier' : 'manager');
      return;
    }
    if (!plain) return;
    const target = event.target as HTMLElement | null;
    const root = this.cashierSearchInput.nativeElement.closest('app-order-list');
    if (!target || !root?.contains(target)) return;
    if (event.key === 'Escape') {
      if (event.repeat) return;
      this.keyboardHelpTooltip?.hide(0);
      if (this.expandedElement) {
        event.preventDefault();
        this.expandedElement = null;
        this.focusOrderSearch('cashier');
        this.cdr.markForCheck();
      }
      return; // With no expanded row, let the global page-back shortcut run.
    }
    if (this.loading) return;
    const rows = this.keyboardRows();
    if (event.key === 'ArrowDown' && (target === this.cashierSearchInput.nativeElement ||
      target === this.managerSearchInput?.nativeElement) && rows.length) {
      event.preventDefault();
      this.focusOrderRow(rows[0]);
      return;
    }
    // Buttons, links and fields inside a row keep their native keyboard behaviour.
    const index = rows.indexOf(target);
    if (index < 0) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.focusOrderRow(rows[Math.max(0, Math.min(rows.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)))]);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (event.repeat) return;
      const row = this.dataSource.find(item => item.id === Number(target.getAttribute('data-keyboard-order-id')));
      if (row) void this.handleOpenDoc(row);
    }
  }

  supplyStatusOptions: { value: OrderSupplyStatus; label: string }[] = [
    { value: 'WAITING_SUPPLY', label: getOrderSupplyStatusLabel('WAITING_SUPPLY') },
    { value: 'READY', label: getOrderSupplyStatusLabel('READY') }
  ];

  paymentStatusOptions: { value: OrderPaymentStatus; label: string }[] = [
    { value: 'UNPAID', label: getOrderPaymentStatusLabel('UNPAID') },
    { value: 'PARTIALLY_PAID', label: getOrderPaymentStatusLabel('PARTIALLY_PAID') },
    { value: 'PAID', label: getOrderPaymentStatusLabel('PAID') }
  ];

  shipmentStatusOptions: { value: OrderShipmentStatus; label: string }[] = [
    { value: 'UNSHIPPED', label: getOrderShipmentStatusLabel('UNSHIPPED') },
    { value: 'PARTIALLY_SHIPPED', label: getOrderShipmentStatusLabel('PARTIALLY_SHIPPED') },
    { value: 'FULLY_SHIPPED', label: getOrderShipmentStatusLabel('FULLY_SHIPPED') }
  ];

  procurementStatusOptions: { value: OrderProcurementStatus; label: string }[] = [
    { value: 'NEEDS_PROCESSING', label: 'Нужно обработать' },
    { value: 'PARTIALLY_ORDERED', label: 'Заказано частично' },
    { value: 'WAITING_DELIVERY', label: 'Ждём поставку' },
    { value: 'PARTIALLY_RECEIVED', label: 'Пришло частично' },
    { value: 'RECEIVED', label: 'Получено' },
    { value: 'NOT_REQUIRED', label: 'Закупка не нужна' }
  ];

  fulfillmentStatusOptions: { value: OrderFulfillmentStatus; label: string }[] = [
    { value: 'PROCESSING', label: 'В обработке' },
    { value: 'WAITING_DELIVERY', label: 'Ждём поставку' },
    { value: 'PUTAWAY_REQUIRED', label: 'Нужно разложить' },
    { value: 'READY_TO_PICK', label: 'Можно собирать' },
    { value: 'PICKING', label: 'Собирается' },
    { value: 'READY_FOR_CLIENT', label: 'Готов клиенту' },
    { value: 'PARTIALLY_SHIPPED', label: 'Отгружен частично' },
    { value: 'COMPLETED', label: 'Завершён' }
  ];

  dataSource: OrderRow[] = [];

  pageSize = 50;

  resultsLength = 0;

  loadingMore = false;
  loadMoreError = false;
  hasMoreOrders = false;

  private nextOffset = 0;
  private appliedSearchPayload: SearchOrderDto | null = null;
  private loadMoreToken: symbol | null = null;
  private loadMoreCheckTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly journalColumns = [
    'ID', 'Ожидаем', 'Готовность', 'clientWithPhone',
    'Оплата статус', 'Отгрузка статус', 'Сумма заказа', 'К оплате'
  ];

  columnsToDisplay = [...this.journalColumns];

  filtersOverlay = typeof window !== 'undefined' && window.innerWidth < 1500;
  mobileLayout = typeof window !== 'undefined' && window.innerWidth <= 767;

  @HostListener('window:resize')
  updateFilterLayout(): void {
    this.filtersOverlay = window.innerWidth < 1500;
    this.mobileLayout = window.innerWidth <= 767;
    this.cdr.markForCheck();
    this.scheduleLoadMoreCheck();
  }

  private updateOrderColumns(): void {
    this.columnsToDisplay = this.journalColumns.filter(column =>
      !this.isClientContext || column !== 'clientWithPhone');
  }

  displayedColumns: string[] = [
    'index',
    'description',
    'article',
    'brand',
    'provider',
    'stock',
    'quan',
    'shipped_quan',
    'remaining_to_ship_quan',
    'waiting_supply_quan',
    'price',
    'sum'
  ];

  expandedElement: OrderRow | null = null;

  deviceType: DeviceTypeEnum = DeviceTypeEnum.DESKTOP;

  searchControl = new FormControl('', { nonNullable: true });

  activeManagerQuery = '';

  lookupControl = new FormControl('', { nonNullable: true });

  activeLookupQuery = '';

  private appliedLookupQuery = '';

  extraFiltersOpened = false;

  private appliedQuickFilterKey = '';

  orderSubscription: Subscription;

  orderStatusSubscription: Subscription;

  private readonly liveSubscriptions = new Subscription();
  private readonly pendingOrderRefreshIds = new Set<number>();
  private liveRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private liveRefreshRunning = false;
  private resyncAfterSearch = false;
  private destroyed = false;

  user: User | undefined;

  loading = false;

  busyByOrderId: Record<number, Partial<Record<OrderActionKey, boolean>>> = {};

  thermalMenuOrder: OrderRow | null = null;

  thermalMenuPosition = {
    x: '0px',
    y: '0px'
  };

  assemblyMenuOrder: OrderRow | null = null;

  assemblyMenuPosition = {
    x: '0px',
    y: '0px'
  };

  productLoadingByOrderId: Record<number, boolean> = {};

  supplyByOrderId: Record<number, OrderSupplyResponse | undefined> = {};
  supplyLoadingByOrderId: Record<number, boolean> = {};
  supplyErrorByOrderId: Record<number, boolean> = {};
  supplyRefreshVersionByOrderId: Record<number, number> = {};

  searchForm: UntypedFormGroup = this.fb.group({
    user: new UntypedFormControl(),
    date_from: new UntypedFormControl(),
    date_to: new UntypedFormControl(),
    id: new UntypedFormControl(),
    client: new UntypedFormControl(),
    warehouse: new UntypedFormControl(),
    is_canceled: new UntypedFormControl(null),
    supply_status: new UntypedFormControl([]),
    procurement_status: new UntypedFormControl([]),
    fulfillment_status: new UntypedFormControl([]),
    payment_status: new UntypedFormControl([]),
    shipment_status: new UntypedFormControl([])
  });

  private storeSubscription: Subscription | undefined;

  private orderCacheById: Record<number, Order> = {};

  private searchRequestId = 0;

  private lastAppliedSearchRequestId = 0;

  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  private deliveryClock: ReturnType<typeof setInterval> | null = null;

  private lastStoreSearchKey = '';

  private productLoadPromiseByOrderId: Record<number, Promise<void> | undefined> = {};

  private productLoadTokenByOrderId: Record<number, symbol | undefined> = {};

  private supplyLoadTokenByOrderId: Record<number, symbol | undefined> = {};

  private supplyLoadPromiseByOrderId: Record<
    number,
    Promise<OrderSupplyResponse | undefined> | undefined
  > = {};

  private terminalRequestStates = new Map<number, StableRequestIdState>();

  readonly productImageUrls = new Map<number, string>();
  private readonly failedProductImages = new Set<string>();
  private readonly requestedImageIds = new Set<number>();
  private readonly imageAttempts = new Map<number, number>();
  private readonly imageRetryTimers = new Set<ReturnType<typeof setTimeout>>();
  private imagesLoading = false;
  hoveredImageUrl: string | null = null;
  hoveredImageTitle = '';
  imagePreviewLeft = 0;
  imagePreviewTop = 0;
  imagePreviewSize = 320;

  constructor(
    private orderService: OrderService,
    private router: Router,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    public supportService: SupportService,
    private fb: UntypedFormBuilder,
    private store: Store<AppState>,
    private titleService: Title,
    private computerService: ComputerService,
    private thermalPrintTargetService: ThermalPrintTargetService,
    private orderInvoiceService: OrderInvoiceService,
    private clientInvoiceService: ClientInvoiceService,
    private shipmentByInvoiceService: ShipmentByInvoiceService,
    private terminalPaymentService: TerminalPaymentService,
    private cdr: ChangeDetectorRef,
    private catalog: NewCatalogService
  ) {
    this.orderSubscription = this.orderService.orderGW.subscribe((orders) => {
      // Legacy payloads and ID-only events share one queue, including during a page search.
      (orders || []).forEach((order) => this.refreshVisibleOrder(order.id));
    });
    this.liveSubscriptions.add(this.orderService.updateOrderGW.subscribe((order) => {
      if (order?.id) this.refreshVisibleOrder(order.id);
    }));
    this.liveSubscriptions.add(this.orderService.ordersChangedGW.subscribe((event) => {
      (event?.order_ids || []).forEach((id) => this.refreshVisibleOrder(id));
    }));
    this.liveSubscriptions.add(this.orderService.socketConnectedGW.subscribe(() => {
      // Socket.IO emits connect again after a reconnect; restore missed changes silently.
      if (this.loading || this.loadingMore) this.resyncAfterSearch = true;
      this.dataSource.forEach((row) => this.refreshVisibleOrder(row.id));
    }));
    this.orderStatusSubscription = this.orderService.orderStatusChangedGW.subscribe((event) => {
      if (event?.order_id) {
        this.refreshVisibleOrder(event.order_id);
      }
    });
  }

  getPartsSelectionLink(description: string | null | undefined): PartsSelectionOrderLink | null {
    const value = String(description || '').trim();
    const match = value.match(/^Документ подбора #(\d+)$/);
    if (!match) {
      return null;
    }

    return {
      id: Number(match[1]),
      label: value
    };
  }

  hasNovaPoshtaDelivery(order: Pick<OrderLight, 'delivery'> | null | undefined): boolean {
    return (
      order?.delivery?.method === OrderDeliveryMethod.NOVA_POSHTA && !!order.delivery.novaPoshta
    );
  }

  getNovaPoshtaKindLabel(order: Pick<OrderLight, 'delivery'>): string {
    return order.delivery?.novaPoshta?.kind === NovaPoshtaDeliveryKind.POSTOMAT
      ? 'Поштомат'
      : 'Відділення';
  }

  getNovaPoshtaWarehouseNumber(order: Pick<OrderLight, 'delivery'>): string {
    const delivery = order.delivery?.novaPoshta;
    const explicitNumber = String(delivery?.warehouseNumber || '').trim();
    if (explicitNumber) {
      return explicitNumber;
    }

    const description = String(delivery?.warehouseDescription || '');
    const match = description.match(/№\s*([0-9]+)/);
    return match?.[1] || '';
  }

  getNovaPoshtaWarehouseAddress(order: Pick<OrderLight, 'delivery'>): string {
    const delivery = order.delivery?.novaPoshta;
    return String(delivery?.warehouseShortAddress || delivery?.warehouseDescription || '').trim();
  }

  getNovaPoshtaCityLine(order: Pick<OrderLight, 'delivery'>): string {
    const delivery = order.delivery?.novaPoshta;
    const parts = [delivery?.cityName, delivery?.areaName, delivery?.regionName]
      .map((value) => String(value || '').trim())
      .filter(Boolean);
    return [...new Set(parts)].join(', ');
  }

  getNovaPoshtaRecipientLine(order: Pick<OrderLight, 'delivery'>): string {
    const delivery = order.delivery?.novaPoshta;
    return [delivery?.recipientFirstName, delivery?.recipientLastName]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .join(' ');
  }

  getOrderComment(description: string | null | undefined): string {
    return String(description || '')
      .replace(/^B2C checkout: (Нова пошта|Самовивіз).*?(\r?\n|$)/, '')
      .trim();
  }

  get id() {
    return this.searchForm.value.id;
  }

  get date_from() {
    return this.searchForm.value.date_from;
  }

  get date_to() {
    return this.searchForm.value.date_to;
  }

  get user_search() {
    return this.searchForm.value.user;
  }

  get client() {
    return this.searchForm.value.client;
  }

  get warehouse() {
    return this.searchForm.value.warehouse;
  }

  get supply_status() {
    return this.searchForm.value.supply_status as OrderSupplyStatus[];
  }

  get payment_status() {
    return this.searchForm.value.payment_status as OrderPaymentStatus[];
  }

  get procurement_status() {
    return this.searchForm.value.procurement_status as OrderProcurementStatus[];
  }

  get fulfillment_status() {
    return this.searchForm.value.fulfillment_status as OrderFulfillmentStatus[];
  }

  get shipment_status() {
    return this.searchForm.value.shipment_status as OrderShipmentStatus[];
  }

  get is_canceled(): boolean | undefined {
    const value = this.searchForm.value.is_canceled;
    return typeof value === 'boolean' ? value : undefined;
  }

  get userControl() {
    return this.searchForm.controls.user as UntypedFormControl;
  }

  get clientControl() {
    return this.searchForm.controls.client as UntypedFormControl;
  }

  get warehouseControl() {
    return this.searchForm.controls.warehouse as UntypedFormControl;
  }

  get extraFilterCount(): number {
    return Number(Boolean(this.client?.id)) + Number(Boolean(this.id)) +
      Number(Boolean(this.user_search?.id && this.user_search.id !== this.user?.id));
  }

  get managerFilterCount(): number {
    return Number(Boolean(this.user_search?.id)) + Number(Boolean(this.warehouse?.id)) +
      Number(Boolean(this.date_from || this.date_to)) + Number(Boolean(this.client?.id)) +
      Number(Boolean(this.id)) + Number(this.is_canceled !== undefined) + [this.supply_status, this.procurement_status,
        this.fulfillment_status, this.payment_status, this.shipment_status]
        .filter(values => values?.length).length;
  }

  private quickFilterKey(): string {
    const { date_from, date_to } = this.searchForm.value;
    return JSON.stringify([this.warehouse?.id || null, this.fulfillment_status || [], date_from, date_to]);
  }

  applyQuickFilters() {
    if (this.searchForm.invalid) return;
    const key = this.quickFilterKey();
    if (key === this.appliedQuickFilterKey) return;
    this.appliedQuickFilterKey = key;
    return this.applyFilters();
  }

  clearQuickWarehouse() {
    this.warehouseControl.setValue(null);
    return this.applyQuickFilters();
  }

  clearQuickPeriod() {
    this.searchForm.patchValue({ date_from: null, date_to: null });
    return this.applyQuickFilters();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.resetLoadMoreState();
    this.imageRetryTimers.forEach(timer => clearTimeout(timer));
    this.imageRetryTimers.clear();
    this.hideImagePreview();
    if (this.initialFocusTimer !== null) clearTimeout(this.initialFocusTimer);
    this.liveSubscriptions.unsubscribe();
    this.pendingOrderRefreshIds.clear();
    if (this.liveRefreshTimer) clearTimeout(this.liveRefreshTimer);
    if (this.orderSubscription) {
      this.orderSubscription.unsubscribe();
    }
    if (this.orderStatusSubscription) {
      this.orderStatusSubscription.unsubscribe();
    }
    if (this.storeSubscription) {
      this.storeSubscription.unsubscribe();
    }
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }
    if (this.deliveryClock) clearInterval(this.deliveryClock);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.context && !changes.clientId) return;
    this.updateOrderColumns();
    // A profile can change while list/detail/socket requests for its previous client are pending.
    ++this.searchRequestId;
    this.pendingOrderRefreshIds.clear();
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    const cachedIds = new Set([
      ...this.dataSource.map(row => row.id),
      ...Object.keys(this.supplyByOrderId).map(Number),
      ...Object.keys(this.productLoadTokenByOrderId).map(Number),
      ...Object.keys(this.supplyLoadTokenByOrderId).map(Number)
    ]);
    cachedIds.forEach(id => this.invalidateOrderDetails(id));
    this.orderCacheById = {};
    this.dataSource = [];
    this.keyboardOrderId = null;
    this.expandedElement = null;
    this.resultsLength = 0;
    this.resetLoadMoreState();
    this.loading = false;
    this.searchControl.setValue('');
    this.lookupControl.setValue('');
    this.activeManagerQuery = '';
    this.activeLookupQuery = '';
    if (changes.context?.firstChange && this.isClientContext) this.pageSize = 30;
    if (this.initialized) void this.search(false);
  }

  ngOnInit(): void {
    this.initialized = true;
    if (!this.isClientContext) this.titleService.setTitle('Заказы');
    this.deliveryClock = setInterval(() => this.refreshDeliveryDeadlines(), 30_000);
    this.storeSubscription = this.store.subscribe((data) => {
      this.deviceType = data.user.deviceType;
      this.user = data.user.info;
      const userId = this.user?.id ?? null;
      if (userId !== this.defaultsOwnerId) {
        this.defaultsOwnerId = userId;
        this.searchForm.reset(orderDefaultFormValue(this.user, this.isClientContext), { emitEvent: false });
        this.lookupControl.setValue('');
        this.searchControl.setValue('');
        this.activeLookupQuery = '';
        this.activeManagerQuery = '';
        this.resetLoadMoreState();
        this.dataSource = [];
        this.expandedElement = null;
        ++this.searchRequestId;
      }
      const nextSearchKey = `${this.user?.id || 'none'}:${this.deviceType}`;
      if (nextSearchKey !== this.lastStoreSearchKey) {
        this.lastStoreSearchKey = nextSearchKey;
        this.search(false);
      }
      // Responsive cards retain every product quantity and financial field.
      this.updateOrderColumns();
      this.cdr.markForCheck();
    });
  }

  handleOnlyMeToggle() {
    if (!this.activeLookupQuery && this.searchForm.value.user?.id === this.user?.id) {
      this.searchForm.controls.user.setValue(null);
    } else {
      this.searchForm.controls.user.setValue(this.user);
    }
    this.applyFilters();
  }

  lookupOrders() {
    this.activeLookupQuery = this.lookupControl.value.trim();
    return this.search();
  }

  clearLookup() {
    this.lookupControl.setValue('');
    this.activeLookupQuery = '';
    return this.search();
  }

  applyFilters() {
    if (this.searchForm.invalid) {
      this.searchForm.markAllAsTouched();
      return;
    }
    return this.searchManagerOrders();
  }

  searchManagerOrders() {
    this.lookupControl.setValue('');
    this.activeLookupQuery = '';
    this.activeManagerQuery = this.searchControl.value.trim();
    return this.search();
  }

  clearManagerSearch() {
    this.searchControl.setValue('');
    return this.searchManagerOrders();
  }

  fastSearch() {
    this.loading = true;
  }

  private buildSearchPayload(): SearchOrderDto {
    // Only submitted filters belong to this result set. Dates and arrays must not
    // change underneath later batches when the operator edits an unapplied form.
    const copyDate = (value: any) => value instanceof Date ? new Date(value.getTime()) :
      typeof value?.clone === 'function' ? value.clone() :
      value && typeof value === 'object' ? { ...value } : value;
    return this.isClientContext
      ? { client_id: this.scopedClientId, manager_query: this.activeManagerQuery || undefined }
      : this.activeLookupQuery
      ? { lookup_query: this.activeLookupQuery }
      : {
          id: this.id,
          date_from: copyDate(this.date_from),
          date_to: copyDate(this.date_to),
          client_id: this.client?.id,
          user_id: this.user_search?.id,
          warehouse_id: this.warehouse?.id,
          is_canceled: this.is_canceled,
          supply_status: this.supply_status?.length ? [...this.supply_status] : undefined,
          procurement_status: this.procurement_status?.length ? [...this.procurement_status] : undefined,
          fulfillment_status: this.fulfillment_status?.length ? [...this.fulfillment_status] : undefined,
          payment_status: this.payment_status?.length ? [...this.payment_status] : undefined,
          shipment_status: this.shipment_status?.length ? [...this.shipment_status] : undefined,
          manager_query: this.activeManagerQuery || undefined
        };
  }

  private resetLoadMoreState(): void {
    this.loadingMore = false;
    this.loadMoreError = false;
    this.hasMoreOrders = false;
    this.nextOffset = 0;
    this.appliedSearchPayload = null;
    this.loadMoreToken = null;
    if (this.loadMoreCheckTimer !== null) clearTimeout(this.loadMoreCheckTimer);
    this.loadMoreCheckTimer = null;
  }

  async search(resetList = true, options: { reloadExpandedProducts?: boolean } = {}) {
    if (!this.user || (this.isClientContext && !this.scopedClientId)) return;
    const payload = !resetList && this.appliedSearchPayload
      ? this.appliedSearchPayload : this.buildSearchPayload();
    if (!payload.lookup_query && (resetList || !this.appliedSearchPayload)) {
      this.appliedQuickFilterKey = this.quickFilterKey();
    }
    // Action refreshes keep the loaded range instead of collapsing it to one batch.
    const targetCount = resetList ? this.pageSize :
      Math.max(this.pageSize, this.nextOffset, this.dataSource.length);
    const requestId = ++this.searchRequestId;
    const lookupQuery = payload.lookup_query || '';
    this.resetLoadMoreState();

    const expandedOrderId = this.expandedElement?.id ?? null;
    const previousExpandedRow = this.expandedElement;

    this.loading = true;
    this.cdr.markForCheck();
    try {
      const orders: OrderLight[] = [];
      const seenIds = new Set<number>();
      let offset = 0;
      let total = 0;
      let hasMore = false;
      do {
        const res = await firstValueFrom(this.orderService.search({
          ...payload, limit: this.pageSize, offset
        }));
        if (this.destroyed || requestId !== this.searchRequestId) return;
        for (const order of res.data) {
          if (!seenIds.has(order.id)) {
            seenIds.add(order.id);
            orders.push(order);
          }
        }
        offset += res.data.length;
        total = res.total;
        hasMore = res.data.length === this.pageSize && offset < total;
      } while (hasMore && offset < targetCount);

      this.lastAppliedSearchRequestId = requestId;
      this.appliedLookupQuery = lookupQuery;
      this.appliedSearchPayload = payload;
      this.nextOffset = offset;
      this.hasMoreOrders = hasMore;
      this.orderCacheById = {};
      this.dataSource = orders.map((order) => {
        const preparedRow = this.prepareOrderRow(order);

        if (
          previousExpandedRow?.id === preparedRow.id &&
          previousExpandedRow.products.length &&
          !preparedRow.products.length
        ) {
          preparedRow.products = previousExpandedRow.products;
        }

        return preparedRow;
      });
      this.resultsLength = total;
      if (resetList && this.ordersScroll) this.ordersScroll.nativeElement.scrollTop = 0;
      if (!this.dataSource.some(row => row.id === this.keyboardOrderId)) this.keyboardOrderId = null;
      if (expandedOrderId !== null) {
        const expandedRow = this.dataSource.find((row) => row.id === expandedOrderId) || null;
        this.expandedElement = expandedRow;
        if (expandedRow) {
          if (options.reloadExpandedProducts) {
            void this.loadProductsForRow(expandedRow, { force: true, requestId });
            void this.loadSupplyForOrder(expandedRow.id, true);
          }
        }
      } else {
        this.expandedElement = null;
      }
    } catch (err) {
      if (this.destroyed || requestId !== this.searchRequestId) {
        return;
      }
      console.error(err);
      this.notificationService.warning(extractApiErrorMessage(err, 'Ошибка загрузки заказов'));
    } finally {
      if (requestId === this.searchRequestId) {
        this.loading = false;
        if (this.resyncAfterSearch) {
          this.resyncAfterSearch = false;
          this.dataSource.forEach((row) => this.refreshVisibleOrder(row.id));
        }
        this.scheduleLiveRefresh();
        this.scheduleLoadMoreCheck();
      }
      this.cdr.markForCheck();
    }
  }

  onOrdersScroll(): void {
    this.hideImagePreview();
    const scroll = this.ordersScroll?.nativeElement;
    if (!scroll || scroll.clientHeight <= 0 || this.loadMoreError) return;
    if (scroll.scrollHeight - scroll.scrollTop - scroll.clientHeight <= 160) {
      void this.loadMoreOrders();
    }
  }

  private scheduleLoadMoreCheck(): void {
    if (this.destroyed || this.loading || this.loadingMore || !this.hasMoreOrders ||
      this.loadMoreError || this.loadMoreCheckTimer !== null) return;
    // Wait for the OnPush table to render; short lists should fill the viewport
    // even when there is not yet enough content to produce a scroll event.
    this.loadMoreCheckTimer = setTimeout(() => {
      this.loadMoreCheckTimer = null;
      this.onOrdersScroll();
    });
  }

  async loadMoreOrders(): Promise<void> {
    if (this.destroyed || this.loading || this.loadingMore || !this.hasMoreOrders ||
      !this.appliedSearchPayload || !this.user || (this.isClientContext && !this.scopedClientId)) return;
    const requestId = this.searchRequestId;
    const token = Symbol('orders-batch');
    const offset = this.nextOffset;
    const payload = this.appliedSearchPayload;
    this.loadMoreToken = token;
    this.loadingMore = true;
    this.loadMoreError = false;
    this.cdr.markForCheck();
    try {
      const res = await firstValueFrom(this.orderService.search({
        ...payload, limit: this.pageSize, offset
      }));
      if (this.destroyed || requestId !== this.searchRequestId || token !== this.loadMoreToken) return;
      // Existing row objects, expanded products and live updates stay intact.
      const seenIds = new Set(this.dataSource.map(row => row.id));
      const appended: OrderRow[] = [];
      for (const order of res.data) {
        if (!seenIds.has(order.id)) {
          seenIds.add(order.id);
          appended.push(this.prepareOrderRow(order));
        }
      }
      this.dataSource = [...this.dataSource, ...appended];
      this.nextOffset = offset + res.data.length;
      this.resultsLength = res.total;
      this.hasMoreOrders = res.data.length === this.pageSize && this.nextOffset < res.total;
    } catch (error) {
      if (this.destroyed || requestId !== this.searchRequestId || token !== this.loadMoreToken) return;
      console.error(error);
      // Keep the cursor for an explicit retry; scrolling must not cause a retry loop.
      this.loadMoreError = true;
    } finally {
      if (!this.destroyed && requestId === this.searchRequestId && token === this.loadMoreToken) {
        this.loadMoreToken = null;
        this.loadingMore = false;
        if (this.resyncAfterSearch) {
          this.resyncAfterSearch = false;
          this.dataSource.forEach(row => this.refreshVisibleOrder(row.id));
        }
        this.scheduleLiveRefresh();
        this.scheduleLoadMoreCheck();
        this.cdr.markForCheck();
      }
    }
  }

  resetSearch() {
    this.activeManagerQuery = '';
    this.lookupControl.setValue('');
    this.activeLookupQuery = '';
    this.searchForm.reset(orderDefaultFormValue(this.user, this.isClientContext));
    this.searchControl.setValue('');
    this.search();
  }

  async openDefaultFilters(): Promise<void> {
    if (this.isClientContext || !this.user || this.defaultsDialogOpened) return;
    const ownerId = this.user.id;
    const labels: Record<string, string> = {};
    const groups = { supply_status: this.supplyStatusOptions, procurement_status: this.procurementStatusOptions,
      fulfillment_status: this.fulfillmentStatusOptions, payment_status: this.paymentStatusOptions,
      shipment_status: this.shipmentStatusOptions };
    Object.entries(groups).forEach(([key, options]) =>
      options.forEach(option => labels[`${key}:${option.value}`] = option.label));
    this.defaultsDialogOpened = true;
    try {
      const result = await firstValueFrom(this.dialog.open(OrderDefaultsDialogComponent, {
        width: '590px', maxWidth: '96vw', autoFocus: false,
        data: { user: this.user, current: captureOrderDefaults(this.searchForm.value, this.user), labels }
      }).afterClosed());
      if (result?.saved && !this.destroyed && this.user?.id === ownerId) this.resetSearch();
    } finally {
      this.defaultsDialogOpened = false;
      this.cdr.markForCheck();
    }
  }

  toggleFab() {
    if (this.isClientContext) {
      if (this.scopedClientId) {
        this.router.navigate(['/orders/create'], { queryParams: { client_id: this.scopedClientId } });
      }
      return;
    }
    this.router.navigateByUrl(`/orders/create`);
  }

  getSum(order: Order) {
    let sum = 0;
    order.products.forEach((prod) => {
      sum = sum + prod.quan * prod.price;
    });

    return sum;
  }

  isActionLoading(orderId: number, action: OrderActionKey) {
    return this.busyByOrderId[orderId]?.[action] === true;
  }

  hasPlannedSupplyForOrder(orderId: number) {
    return this.getPlannedSupplyAllocations(orderId).length > 0;
  }

  hasUnrequestedSupplyForOrder(orderId: number) {
    return this.getPlannedSupplyAllocations(orderId).some(allocation => !allocation.needs_order);
  }

  async requestProcurementFromList(order: OrderRow, event?: Event) {
    this.stopAction(event);
    await this.runOrderAction(order.id, 'request_procurement', async () => {
      const result = await firstValueFrom(this.orderService.requestProcurement(order.id));
      this.supplyByOrderId[order.id] = result.supply;
      this.notificationService.notice(`Передано в закупку: ${result.buy_ids.length}`);
      delete this.orderCacheById[order.id];
      await this.search(false, { reloadExpandedProducts: true });
    });
  }

  async confirmProcurementFromList(order: OrderRow, event?: Event) {
    this.stopAction(event);

    await this.runOrderAction(order.id, 'confirm_procurement', async () => {
      const supply = await this.loadSupplyForOrder(order.id, true);
      const planned = this.getPlannedSupplyAllocations(order.id, supply);
      if (!planned.length) {
        this.notificationService.warning('Нет выбранных поставщиков для подтверждения');
        return;
      }

      const quantityByProvider = new Map<string, number>();
      planned.forEach((allocation) => {
        const provider = allocation.provider || `Поставщик #${allocation.provider_id}`;
        quantityByProvider.set(
          provider,
          (quantityByProvider.get(provider) || 0) + Number(allocation.quantity || 0)
        );
      });
      const providerSummary = [...quantityByProvider.entries()]
        .map(([provider, quantity]) => `${provider} — ${this.formatQuantity(quantity)} шт.`)
        .join('\n');
      const total = planned.reduce((sum, allocation) => sum + Number(allocation.quantity || 0), 0);
      if (
        !window.confirm(
          `Подтвердить, что ${this.formatQuantity(total)} шт. уже заказаны у поставщиков?\n\n${providerSummary}`
        )
      ) {
        return;
      }

      const providerContracts: Array<{ provider_id: number; contract_id: number }> = [];
      const operationId = createRequestId();
      let result: { buy_ids: number[]; supply: OrderSupplyResponse } | undefined;

      while (!result) {
        try {
          result = await firstValueFrom(
            this.orderService.confirmProcurement(
              order.id,
              planned.map((allocation) => allocation.id),
              providerContracts,
              operationId
            )
          );
        } catch (error) {
          const response = (error as any)?.error;
          const details = response?.code ? response : response?.message;
          if (details?.code !== 'CONTRACT_SELECTION_REQUIRED') throw error;

          const contracts = details.contracts || [];
          const choice = window.prompt(
            `Выберите договор:\n${contracts
              .map((contract: any) => `${contract.id} — ${contract.number}`)
              .join('\n')}\n\nВведите номер слева:`
          );
          const contractId = Number(choice);
          if (!contracts.some((contract: any) => Number(contract.id) === contractId)) {
            this.notificationService.warning('Заказы поставщикам не созданы');
            return;
          }
          providerContracts.push({
            provider_id: Number(details.provider_id),
            contract_id: contractId
          });
        }
      }

      this.supplyByOrderId[order.id] = result.supply;
      this.notificationService.notice(`Создано заказов поставщикам: ${result.buy_ids.length}`);
      delete this.orderCacheById[order.id];
      await this.search(false, { reloadExpandedProducts: true });
    });
  }

  stopAction(event?: Event) {
    event?.stopPropagation();
  }

  async deleteDoc(order: OrderRow, event?: Event) {
    this.stopAction(event);
    if (order.source === 'B2C') {
      if (!order.is_canceled) await this.cancelOrder(order);
      return;
    }
    await this.runOrderAction(order.id, 'delete', async () => {
      await firstValueFrom(this.orderService.delete(order.id));
      this.notificationService.notice(`Заказ № ${order.id} удален!`);
      await this.search(false);
    });
  }

  async openOrderRefusal(order: OrderRow, event?: Event) {
    this.stopAction(event);
    const changed = await firstValueFrom(this.dialog.open(OrderRefusalDialogComponent, {
      width: '680px', maxWidth: '96vw', data: { orderId: order.id }
    }).afterClosed());
    if (changed) {
      delete this.orderCacheById[order.id];
      await this.search(false, { reloadExpandedProducts: true });
    }
  }

  async cancelOrder(order: OrderRow, event?: Event) {
    await this.openOrderRefusal(order, event);
  }

  async copyOrder(id: number, event?: Event) {
    this.stopAction(event);
    await this.runOrderAction(id, 'copy', async () => {
      await firstValueFrom(this.orderService.copyOrder(id));
      this.notificationService.notice(`Заказ №${id} копировано!`);
      await this.search();
    });
  }

  async updateManager(orderId: number, event?: Event) {
    this.stopAction(event);
    if (!this.user) return;

    await this.runOrderAction(orderId, 'take_manager', async () => {
      await firstValueFrom(this.orderService.claim(orderId));
      this.notificationService.notice(`Заказ №${orderId} взят в обработку`);
      await this.search(false);
    });
  }

  async handleOpenDoc(row: OrderRow) {
    this.hideImagePreview();
    if (this.isDetailExpanded(row)) {
      this.expandedElement = null;
      this.cdr.markForCheck();
      return;
    }

    this.expandedElement = row;
    this.cdr.markForCheck();

    await Promise.all([this.loadProductsForRow(row), this.loadSupplyForOrder(row.id)]);
  }

  isDetailProductsReady(row: OrderRow) {
    return row.products.length > 0;
  }

  shouldShowDetailLoader(row: OrderRow) {
    return this.expandedElement?.id === row.id && this.productLoadingByOrderId[row.id] === true;
  }

  isDetailFrameVisible(row: OrderRow) {
    // Open immediately so the user sees the composition loading state.
    return this.isDetailExpanded(row);
  }

  isDetailExpanded(row: OrderRow) {
    return this.expandedElement?.id === row.id;
  }

  async editOrder(order: OrderRow, event?: Event) {
    this.stopAction(event);
    await this.runOrderAction(order.id, 'edit', async () => {
      await this.router.navigate(['/orders/update', order.id]);
    });
  }

  async createPartsSelection(order: OrderRow, event?: Event) {
    this.stopAction(event);
    if (this.destroyed || this.isActionLoading(order.id, 'create_parts_selection')) return;

    const context = this.context;
    const scopedClientId = this.scopedClientId;
    await this.runOrderAction(order.id, 'create_parts_selection', async () => {
      // The list has car snapshots only; read the current relation rather than guessing by VIN.
      const details = await firstValueFrom(this.orderService.getOne(order.id));
      if (this.destroyed || this.context !== context || this.scopedClientId !== scopedClientId) return;

      const clientId = Number(details.client?.id);
      const clientCarId = Number(details.client_car?.id);
      if (!Number.isInteger(clientId) || clientId <= 0) {
        this.notificationService.warning('Для нового подбора сначала укажите клиента в заказе');
        return;
      }
      if (!Number.isInteger(clientCarId) || clientCarId <= 0) {
        this.notificationService.warning('Для нового подбора сначала укажите автомобиль в заказе');
        return;
      }

      await this.router.navigate(['/parts-selection', 'new'], {
        queryParams: { client_id: clientId, client_car_id: clientCarId }
      });
    });
  }

  async createSale(order: OrderRow, event?: Event) {
    this.stopAction(event);
    await this.runOrderAction(order.id, 'create_sale', async () => {
      await this.router.navigate(['/docs/export/create/by_order', order.id]);
    });
  }

  async createInvoice(order: OrderRow, event?: Event) {
    this.stopAction(event);
    await this.runOrderAction(order.id, 'create_invoice', async () => {
      await this.router.navigate(['/docs/client_invoice/create'], {
        queryParams: { order_id: order.id }
      });
    });
  }

  printOrder(order: Order, withArticles = false, agentName?: string) {
    return this.computerService.printOrder({ order, withArticles, agentName });
  }

  isThermalPrintLoading(orderId: number) {
    return (
      this.isActionLoading(orderId, 'print_order_thermal') ||
      this.isActionLoading(orderId, 'print_order_thermal_articles')
    );
  }

  async printOrderThermal(order: OrderRow, event: Event, withArticles = false) {
    this.stopAction(event);
    const action: OrderActionKey = withArticles
      ? 'print_order_thermal_articles'
      : 'print_order_thermal';

    await this.runOrderAction(order.id, action, async () => {
      const fullOrder = await this.getOrderDetails(order.id);
      await this.printOrder(fullOrder, withArticles);
      this.notificationService.notice(
        withArticles
          ? `Заказ №${order.id} отправлен на термопечать с артикулами`
          : `Заказ №${order.id} отправлен на термопечать`
      );
    });
  }

  async printOrderThermalToAnotherAgent(order: OrderRow, event: Event, withArticles = false) {
    this.stopAction(event);
    const action: OrderActionKey = withArticles
      ? 'print_order_thermal_articles'
      : 'print_order_thermal';

    await this.runOrderAction(order.id, action, async () => {
      const agentName = await this.thermalPrintTargetService.chooseAgent(this.user);
      if (!agentName) {
        return;
      }

      const fullOrder = await this.getOrderDetails(order.id);
      await this.printOrder(fullOrder, withArticles, agentName);
      this.notificationService.notice(
        withArticles
          ? `Заказ №${order.id} отправлен на термопечать с артикулами в агент ${agentName}`
          : `Заказ №${order.id} отправлен на термопечать в агент ${agentName}`
      );
    });
  }

  openOrderThermalMenu(order: OrderRow, event: MouseEvent) {
    event.preventDefault();
    this.stopAction(event);

    if (this.isThermalPrintLoading(order.id)) {
      return;
    }

    this.thermalMenuOrder = order;
    this.thermalMenuPosition = {
      x: `${event.clientX}px`,
      y: `${event.clientY}px`
    };
    this.cdr.detectChanges();
    this.thermalMenuTrigger?.openMenu();
  }

  closeOrderThermalMenu() {
    this.thermalMenuOrder = null;
    this.cdr.markForCheck();
  }

  async handleOrderThermalMenuAction(mode: 'local' | 'agent', withArticles: boolean, event: Event) {
    this.stopAction(event);
    const order = this.thermalMenuOrder;
    this.thermalMenuTrigger?.closeMenu();

    if (!order) {
      return;
    }

    if (mode === 'agent') {
      await this.printOrderThermalToAnotherAgent(order, event, withArticles);
      return;
    }

    await this.printOrderThermal(order, event, withArticles);
  }

  openAssemblySheetMenu(order: OrderRow, event: MouseEvent) {
    event.preventDefault();
    this.stopAction(event);

    if (this.isActionLoading(order.id, 'print_assembly_sheet')) {
      return;
    }

    this.assemblyMenuOrder = order;
    this.assemblyMenuPosition = {
      x: `${event.clientX}px`,
      y: `${event.clientY}px`
    };
    this.cdr.detectChanges();
    this.assemblyMenuTrigger?.openMenu();
  }

  closeAssemblySheetMenu() {
    this.assemblyMenuOrder = null;
    this.cdr.markForCheck();
  }

  async handleAssemblySheetMenuAction(mode: 'local' | 'agent', event: Event) {
    this.stopAction(event);
    const order = this.assemblyMenuOrder;
    this.assemblyMenuTrigger?.closeMenu();

    if (!order) {
      return;
    }

    if (mode === 'agent') {
      await this.printAssemblySheetToAnotherAgent(order, event);
      return;
    }

    await this.printAssemblySheet(order, event);
  }

  async printAssemblySheet(order: OrderRow, event: Event) {
    this.stopAction(event);
    await this.runOrderAction(order.id, 'print_assembly_sheet', async () => {
      const fullOrder = await this.getOrderDetails(order.id);
      const warehouseId = fullOrder.warehouse?.id;
      const products = fullOrder.products.map((prod) => {
        const stockRow = prod.product?.stock?.find(
          (stock: any) => stock?.warehouse?.id === warehouseId
        );
        return {
          id: prod.product?.id,
          description: prod.product?.description || '',
          brand:
            typeof prod.product?.brand === 'string'
              ? prod.product.brand
              : prod.product?.brand?.description || '',
          article: prod.product?.article || '',
          quan: Number(prod.quan),
          cell: stockRow?.cell,
          nal: stockRow?.quan
        };
      });

      this.computerService.printAssemblySheet({
        warehouse: fullOrder.warehouse?.description || order.warehouse,
        warehouseId,
        products
      });
      this.notificationService.notice(`Сборочный лист по заказу №${order.id} отправлен на печать`);
    });
  }

  async printAssemblySheetToAnotherAgent(order: OrderRow, event: Event) {
    this.stopAction(event);
    await this.runOrderAction(order.id, 'print_assembly_sheet', async () => {
      const agentName = await this.thermalPrintTargetService.chooseAgent(this.user);
      if (!agentName) {
        return;
      }

      const fullOrder = await this.getOrderDetails(order.id);
      const warehouseId = fullOrder.warehouse?.id;
      const products = fullOrder.products.map((prod) => {
        const stockRow = prod.product?.stock?.find(
          (stock: any) => stock?.warehouse?.id === warehouseId
        );
        return {
          id: prod.product?.id,
          description: prod.product?.description || '',
          brand:
            typeof prod.product?.brand === 'string'
              ? prod.product.brand
              : prod.product?.brand?.description || '',
          article: prod.product?.article || '',
          quan: Number(prod.quan),
          cell: stockRow?.cell,
          nal: stockRow?.quan
        };
      });

      await this.computerService.printAssemblySheet({
        warehouse: fullOrder.warehouse?.description || order.warehouse,
        warehouseId,
        products,
        agentName
      });
      this.notificationService.notice(
        `Сборочный лист по заказу №${order.id} отправлен в агент ${agentName}`
      );
    });
  }

  async printInvoice(order: OrderRow, type: ClientInvoicePrintType, event: Event) {
    this.stopAction(event);
    const action: OrderActionKey = type === 'id' ? 'print_invoice_id' : 'print_invoice_article';

    await this.runOrderAction(order.id, action, async () => {
      const invoice = await this.resolveInvoice(order.id, 'payment');
      const blob = await firstValueFrom(this.clientInvoiceService.getPdf(invoice.id, type));
      this.openBlobInNewTab(blob);
    });
  }

  openPaymentDialog(order: OrderRow, event: Event) {
    this.stopAction(event);

    this.runOrderAction(order.id, 'create_payment', async () => {
      const dialogRef = this.dialog.open(OrderPaymentDialogComponent, {
        width: '480px',
        maxWidth: '95vw',
        autoFocus: false,
        data: {
          orderId: order.id,
          orderLabel: `Заказ №${order.id}`,
          clientLabel: order.client,
          paymentDue: order.paymentDue,
          invoiceCount: order.invoice_count
        }
      });

      const result = await firstValueFrom(dialogRef.afterClosed());
      if ((result as OrderPaymentDialogResult | undefined)?.createdPaymentId || (result as OrderPaymentDialogResult | undefined)?.accountOperationId) {
        this.scheduleRefresh(false);
      }
    });
  }

  startTerminalPayment(order: OrderRow, event: Event, chooseAgent = false) {
    event.preventDefault();
    this.stopAction(event);

    this.runOrderAction(order.id, 'terminal_payment', async () => {
      const agentName = await this.resolveTerminalPaymentAgent(chooseAgent);
      if (!agentName) {
        return;
      }
      const terminalProfile = await this.resolveTerminalProfile(
        Number(this.user?.defaultWarehouse?.id || 0)
      );
      if (terminalProfile === null) return;

      const attempt = await firstValueFrom(
        this.terminalPaymentService.startOrderPayment(order.id, {
          request_id: this.resolveTerminalRequestId(order, agentName, terminalProfile),
          amount: order.paymentDue,
          agentName,
          terminal_profile_id: terminalProfile?.id,
          provider_batch_id: terminalProfile?.currentProviderBatchId || undefined
        })
      );
      const result = await this.waitForTerminalPayment(
        attempt.id,
        `Оплата заказа №${order.id} через терминал`
      );
      if (result?.isFinal) this.terminalRequestStates.delete(order.id);
      if (result?.status === 'APPROVED') {
        this.notificationService.notice(`Оплата заказа №${order.id} через терминал подтверждена`);
      } else if (result?.isFinal) {
        this.notificationService.warning(
          result.responseMessage || result.error || 'Оплата терминалом не подтверждена'
        );
      }
      this.scheduleRefresh(false);
    });
  }

  private async resolveTerminalPaymentAgent(chooseAgent: boolean) {
    if (chooseAgent) {
      return this.thermalPrintTargetService.chooseTerminalAgent(this.user);
    }

    const agentName = this.thermalPrintTargetService.getDefaultAgentName(this.user);
    if (!agentName) {
      this.notificationService.warning(
        'Агент по умолчанию не выбран. Нажмите правой кнопкой по кнопке «Терминал» и выберите агент.'
      );
      return null;
    }

    return agentName;
  }

  private resolveTerminalRequestId(
    order: OrderRow,
    agentName: string,
    terminalProfile?: TerminalProfile
  ) {
    const next = resolveStableRequestId(
      this.terminalRequestStates.get(order.id),
      JSON.stringify({
        order_id: order.id,
        amount: Number(order.paymentDue || 0),
        agentName,
        terminal_profile_id: terminalProfile?.id || null,
        provider_batch_id: terminalProfile?.currentProviderBatchId || null
      })
    );
    this.terminalRequestStates.set(order.id, next);
    return next.requestId;
  }

  private async resolveTerminalProfile(
    warehouseId: number
  ): Promise<TerminalProfile | null | undefined> {
    const profiles = await firstValueFrom(
      this.terminalPaymentService.getProfiles(warehouseId || undefined)
    );
    if (!profiles.length) return undefined;
    if (profiles.length === 1) return profiles[0];
    return (await firstValueFrom(
      this.dialog
        .open(TerminalProfileSelectDialogComponent, {
          width: '500px',
          maxWidth: '95vw',
          autoFocus: false,
          data: { profiles }
        })
        .afterClosed()
    )) as TerminalProfile | null;
  }

  openPaymentLink(order: OrderRow, event: Event) {
    this.stopAction(event);

    this.runOrderAction(order.id, 'payment_link', async () => {
      const { invoice, created, paymentDue } = await this.orderInvoiceService.ensureInvoiceForOrder(
        order.id,
        new Date(),
        'payment'
      );
      if (created) {
        this.notificationService.notice(`Счет №${invoice.id} создан автоматически`);
      }

      const outstandingAmount =
        paymentDue !== undefined && paymentDue !== null
          ? Number(paymentDue)
          : this.getInvoicePaymentDue(invoice);
      if (!Number.isFinite(outstandingAmount) || outstandingAmount <= 0) {
        this.notificationService.warning('По счету нет остатка к оплате');
        return;
      }

      const dialogRef = this.dialog.open(PaymentLinkDialogComponent, {
        width: '560px',
        maxWidth: '95vw',
        autoFocus: false,
        data: {
          invoiceId: invoice.id,
          invoiceTitle: `счета №${invoice.id}`,
          outstandingAmount
        }
      });

      const result = await firstValueFrom(dialogRef.afterClosed());
      if (result) {
        this.scheduleRefresh(false);
      }
    });
  }

  async openPayments(order: OrderRow, event: Event) {
    this.stopAction(event);
    await this.runOrderAction(order.id, 'open_payments', async () => {
      const invoice = await this.resolveInvoice(order.id, 'payment');
      await this.router.navigate(['/docs/client_invoice_payment/list'], {
        queryParams: { invoice_id: invoice.id }
      });
    });
  }

  async createShipmentByInvoice(order: OrderRow, event: Event) {
    this.stopAction(event);

    await this.runOrderAction(order.id, 'create_shipment_by_invoice', async () => {
      const invoice = await this.resolveInvoice(order.id, 'shipment');
      await this.router.navigate(['/docs/shipment_by_invoice/create'], {
        queryParams: { invoice_id: invoice.id }
      });
    });
  }

  async printShipment(order: OrderRow, type: ShipmentByInvoicePrintType, event: Event) {
    this.stopAction(event);
    const action: OrderActionKey = type === 'id' ? 'print_shipment_id' : 'print_shipment_article';

    await this.runOrderAction(order.id, action, async () => {
      const invoice = await this.resolveInvoice(order.id, 'shipment');
      const shipments = await firstValueFrom(
        this.shipmentByInvoiceService.search({
          invoice_id: invoice.id,
          limit: 1,
          offset: 0
        })
      );
      const shipment = shipments[0];

      if (!shipment?.id) {
        this.notificationService.warning(`Для счета №${invoice.id} не найдена созданная отгрузка`);
        return;
      }

      const blob = await firstValueFrom(this.shipmentByInvoiceService.getPdf(shipment.id, type));
      this.openBlobInNewTab(blob);
    });
  }

  async silentShipment(order: OrderRow, event: Event) {
    this.stopAction(event);

    await this.runOrderAction(order.id, 'silent_shipment', async () => {
      const request = (allowDebtLimitExceeded = false) =>
        firstValueFrom(
          this.orderService.createShipmentFlow(order.id, {
            date: new Date(),
            allowDebtLimitExceeded
          })
        );
      let result;
      try {
        result = await request(false);
      } catch (err) {
        const warning = getClientDebtWarning(err);
        if (!warning) throw err;
        if (!confirmClientDebtWarning(warning)) return;
        result = await request(true);
      }
      const shipments = (result.created_documents || []).filter((doc) => doc.type === 'shipment');
      if (!shipments.length) {
        this.notificationService.warning('Не удалось создать отгрузку');
        return;
      }

      getDebtWarningMessages(result).forEach((message) =>
        this.notificationService.warning(message)
      );
      this.notificationService.notice(
        shipments.length === 1
          ? `Отгрузка №${shipments[0].id} создана по готовым позициям`
          : `Созданы отгрузки: ${shipments.map((doc) => `№${doc.id}`).join(', ')}`
      );
      this.scheduleRefresh(false);
    });
  }

  trackByOrderId(_: number, order: OrderRow) {
    return order.id;
  }

  trackByProduct(_: number, product: ProductRow) {
    return `${product.article || product.description || 'product'}-${product.price}-${
      product.quan
    }`;
  }

  private prepareOrderRow(order: OrderLight): OrderRow {
    const paymentDue = getOrderPaymentDue(order);

    return {
      ...order,
      lookupMatch: orderLookupMatch(this.appliedLookupQuery, order),
      products: this.prepareProductRows(order.products || [], order.id),
      dateLabel: order.date.toLocaleDateString(),
      deliveryDeadline: orderDeliveryHint(order),
      supplyStatusLabel: order.is_canceled
        ? 'Отменен'
        : getOrderSupplyStatusLabel(order.supply_status),
      paymentStatusLabel: getOrderPaymentStatusLabel(order.payment_status, order),
      shipmentStatusLabel: getOrderShipmentStatusLabel(order.shipment_status),
      supplyStatusClass: order.is_canceled
        ? 'status-danger'
        : order.supply_status === 'READY'
        ? 'status-ok'
        : 'status-warn',
      paymentStatusClass: order.payment_status === 'PAID' ? 'status-ok' : 'status-warn',
      shipmentStatusClass: order.shipment_status === 'FULLY_SHIPPED' ? 'status-ok' : 'status-warn',
      paidTotalLabel: this.supportService.formatMoney(order.paid_total),
      sumLabel: this.supportService.formatMoney(order.sum),
      invoiceTotalLabel: this.supportService.formatMoney(order.invoice_total),
      collectibleTotalLabel: this.supportService.formatMoney(order.collectible_total),
      returnedTotalLabel: this.supportService.formatMoney(order.returned_total),
      refundDueLabel: this.supportService.formatMoney(order.refund_due_total),
      paymentDue,
      paymentDueLabel: this.supportService.formatMoney(paymentDue),
      rowClass: this.resolveRowClass(order)
    };
  }

  private prepareProductRows(products: Array<ProductInDoc | OrderProductApiRow>, orderId: number): ProductRow[] {
    return products
      .map((product, index) => ({ product, index }))
      .sort((left, right) => {
        const priorityDiff =
          this.getProductSortPriority(left.product) - this.getProductSortPriority(right.product);
        if (priorityDiff !== 0) {
          return priorityDiff;
        }

        return left.index - right.index;
      })
      .map(({ product }) => this.prepareProductRow(product, orderId));
  }

  private refreshDeliveryDeadlines(now = Date.now()) {
    for (const row of this.dataSource) {
      row.deliveryDeadline = orderDeliveryHint(row, now);
      row.products = row.products.map(product => ({
        ...product,
        delivery: productDeliveryHint(product, this.supplyByOrderId[row.id], now)
      }));
    }
    this.cdr.markForCheck();
  }

  private prepareProductRow(product: ProductInDoc | OrderProductApiRow, orderId: number): ProductRow {
    const reference = {
      orderProductId: 'product' in product ? product.id : product.order_product_id,
      productId: 'product' in product ? product.product?.id : product.id,
      quan: product.quan,
      shipped_quan: product.shipped_quan,
      waiting_supply_quan: product.waiting_supply_quan,
      is_ready: product.is_ready
    };
    const providerLabel =
      typeof product.provider === 'string' ? product.provider : product.provider?.description;
    const description =
      'description' in product ? product.description : product.product?.description;
    const article = 'article' in product ? product.article : product.product?.article;
    const brand =
      'brand' in product
        ? product.brand
        : typeof product.product?.brand === 'string'
        ? product.product.brand
        : product.product?.brand?.description;
    const stock =
      'stock' in product
        ? product.stock
        : Array.isArray(product.product?.stock)
        ? product.product.stock.reduce((total, item) => total + (item.quan || 0), 0)
        : undefined;
    const shippedQuan = product.shipped_quan || 0;
    const remainingToShipQuan = product.remaining_to_ship_quan || 0;
    const waitingSupplyQuan = product.waiting_supply_quan || 0;
    const returnedQuan = product.returned_quan || 0;
    const returnDocIds = Array.isArray(product.return_doc_ids)
      ? product.return_doc_ids
          .map((id) => Number(id))
          .filter((id) => Number.isInteger(id) && id > 0)
      : [];

    return {
      ...reference,
      images: ('product' in product ? product.product?.images : product.images) || [],
      delivery: productDeliveryHint(reference, this.supplyByOrderId[orderId]),
      quan: product.quan,
      price: product.price,
      shipped_quan: product.shipped_quan,
      remaining_to_ship_quan: product.remaining_to_ship_quan,
      waiting_supply_quan: product.waiting_supply_quan,
      returned_quan: returnedQuan,
      return_doc_ids: returnDocIds,
      description,
      article,
      brand,
      provider: providerLabel,
      stock,
      priceLabel: this.supportService.formatMoney(product.price),
      sumLabel: this.supportService.formatMoney(product.price * product.quan),
      shippedQuanClass: shippedQuan > 0 ? 'metric-ok' : 'metric-warn',
      remainingToShipClass: remainingToShipQuan > 0 ? 'metric-warn' : 'metric-ok',
      waitingSupplyClass: waitingSupplyQuan > 0 ? 'metric-danger' : 'metric-ok',
      hasReturns: returnedQuan > 0,
      returnedQuanLabel: returnedQuan > 0 ? this.formatQuantity(returnedQuan) : undefined,
      returnDocIdsLabel: returnDocIds.length
        ? returnDocIds.map((id) => `#${id}`).join(', ')
        : undefined
    };
  }

  private getProductSortPriority(product: ProductInDoc | OrderProductApiRow) {
    const shippedQuan = Number(product.shipped_quan || 0);
    const remainingToShipQuan = Number(product.remaining_to_ship_quan || 0);
    const waitingSupplyQuan = Number(product.waiting_supply_quan || 0);
    const isReady = Boolean(product.is_ready);

    if (shippedQuan <= 0 && waitingSupplyQuan > 0) {
      return 0;
    }

    if (shippedQuan <= 0 && (remainingToShipQuan > 0 || isReady)) {
      return 1;
    }

    if (shippedQuan > 0 && (remainingToShipQuan > 0 || waitingSupplyQuan > 0)) {
      return 2;
    }

    if (shippedQuan > 0) {
      return 3;
    }

    return 4;
  }

  private formatQuantity(value: number) {
    return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
  }

  private resolveRowClass(order: OrderLight) {
    if (order.is_canceled) return 'declineRow';
    if (order.payment_status === 'PAID' && order.shipment_status === 'FULLY_SHIPPED') {
      return 'doneRow';
    }
    if (order.supply_status === 'WAITING_SUPPLY') return 'procRow';
    if (order.supply_status === 'READY') return 'successRow';
    if (order.shipment_status === 'PARTIALLY_SHIPPED') return 'stockRow';
    return 'procRow';
  }

  private setActionLoading(orderId: number, action: OrderActionKey, value: boolean) {
    this.busyByOrderId[orderId] = {
      ...(this.busyByOrderId[orderId] || {}),
      [action]: value
    };
    this.cdr.markForCheck();
  }

  private async runOrderAction(orderId: number, action: OrderActionKey, task: () => Promise<void>) {
    this.setActionLoading(orderId, action, true);
    try {
      await task();
    } catch (err) {
      console.error(err);
      this.notificationService.warning(extractApiErrorMessage(err, 'Ошибка выполнения действия'));
    } finally {
      this.setActionLoading(orderId, action, false);
    }
  }

  private waitForTerminalPayment(attemptId: number, title: string) {
    return firstValueFrom(
      this.dialog
        .open(TerminalPaymentWaitDialogComponent, {
          width: '460px',
          maxWidth: '95vw',
          autoFocus: false,
          disableClose: true,
          data: {
            attemptId,
            title
          }
        })
        .afterClosed()
    ) as Promise<TerminalPaymentAttempt | undefined>;
  }

  private async getOrderDetails(orderId: number) {
    const cachedOrder = this.orderCacheById[orderId];
    if (cachedOrder) {
      return cachedOrder;
    }

    const order = await firstValueFrom(this.orderService.getOne(orderId));
    this.orderCacheById[orderId] = order;
    return order;
  }

  private async resolveInvoice(orderId: number, purpose: 'payment' | 'shipment') {
    const { invoice, created } = await this.orderInvoiceService.ensureInvoiceForOrder(
      orderId,
      new Date(),
      purpose
    );
    if (created) {
      this.notificationService.notice(`Счет №${invoice.id} создан автоматически`);
    }
    return invoice;
  }

  private getInvoicePaymentDue(invoice: {
    total?: number;
    paid_total?: number;
    collectible_total?: number | null;
  }) {
    const collectibleTotal =
      invoice.collectible_total !== undefined && invoice.collectible_total !== null
        ? Number(invoice.collectible_total)
        : Number(invoice.total || 0);
    return Math.max(collectibleTotal - Number(invoice.paid_total || 0), 0);
  }

  private openBlobInNewTab(blob: Blob) {
    const fileURL = URL.createObjectURL(blob);
    window.open(fileURL, '_blank');
    setTimeout(() => URL.revokeObjectURL(fileURL), 10_000);
  }

  private productImageUrl(image: ProductImage): string | null {
    const name = String(image?.name || '').trim();
    if (/^https?:\/\//i.test(name)) return name;
    if (name && !name.startsWith('supplier-remote-') && !name.includes('/')) {
      return `${environment.BACKEND}/image/${encodeURIComponent(name)}`;
    }
    const source = String(image?.url || '').trim();
    return /^https?:\/\//i.test(source) ? source : null;
  }

  private loadProductImages(): void {
    if (this.destroyed) return;
    // Only enrich the open order; preserve its quantities, ordering and fulfillment state.
    const products = this.expandedElement?.products || [];
    for (const product of products) {
      const local = (product.images || []).map(image => this.productImageUrl(image))
        .find(url => url && !this.failedProductImages.has(url));
      if (local) this.productImageUrls.set(Number(product.productId), local);
    }
    if (this.imagesLoading) return;
    const ids = [...new Set(products.map(product => Number(product.productId)))]
      .filter(id => Number.isSafeInteger(id) && id > 0 &&
        !this.productImageUrls.has(id) && !this.requestedImageIds.has(id)).slice(0, 20);
    if (!ids.length) return;
    ids.forEach(id => {
      this.requestedImageIds.add(id);
      this.imageAttempts.set(id, (this.imageAttempts.get(id) || 0) + 1);
    });
    this.imagesLoading = true;
    this.liveSubscriptions.add(this.catalog.getProductImages(ids).pipe(
      finalize(() => {
        this.imagesLoading = false;
        if (this.destroyed) return;
        this.cdr.markForCheck();
        // A cold TecDoc lookup can finish filling the cache after the API timeout.
        const retryIds = ids.filter(id => !this.productImageUrls.has(id) && this.imageAttempts.get(id)! < 2);
        if (retryIds.length) {
          const timer = setTimeout(() => {
            this.imageRetryTimers.delete(timer);
            retryIds.forEach(id => this.requestedImageIds.delete(id));
            this.loadProductImages();
          }, 3000);
          this.imageRetryTimers.add(timer);
        }
        this.loadProductImages();
      })
    ).subscribe({
      next: response => {
        for (const image of response.items || []) {
          if (ids.includes(image.productId) && /^https?:\/\//i.test(image.imageUrl) &&
              !this.failedProductImages.has(image.imageUrl)) {
            this.productImageUrls.set(image.productId, image.imageUrl);
          }
        }
        this.cdr.markForCheck();
      },
      error: () => { /* Optional photos must not block order actions. */ }
    }));
  }

  handleProductImageError(product: ProductRow, url: string): void {
    if (this.hoveredImageUrl === url) this.hideImagePreview();
    this.failedProductImages.add(url);
    if (this.productImageUrls.get(Number(product.productId)) === url) {
      this.productImageUrls.delete(Number(product.productId));
    }
    this.loadProductImages();
  }

  showImagePreview(event: MouseEvent | FocusEvent, product: ProductRow): void {
    const url = this.productImageUrls.get(Number(product.productId));
    if (!url) return;
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const padding = 12;
    const size = Math.max(0, Math.min(320, window.innerWidth - 2 * padding, window.innerHeight - 2 * padding));
    const left = rect.right + padding + size <= window.innerWidth - padding
      ? rect.right + padding : rect.left - size - padding;
    this.imagePreviewSize = size;
    this.imagePreviewLeft = Math.max(padding, Math.min(left, window.innerWidth - size - padding));
    this.imagePreviewTop = Math.max(padding, Math.min(rect.top, window.innerHeight - size - padding));
    this.hoveredImageTitle = product.description || '';
    this.hoveredImageUrl = url;
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  @HostListener('document:keydown.escape')
  hideImagePreview(): void {
    this.hoveredImageUrl = null;
  }

  private loadProductsForRow(row: OrderRow, options: { force?: boolean; requestId?: number } = {}) {
    const { force = false, requestId } = options;

    if (!force && row.products.length > 0) {
      this.loadProductImages();
      return Promise.resolve();
    }

    const existingRequest = this.productLoadPromiseByOrderId[row.id];
    if (existingRequest && !force) {
      return existingRequest;
    }

    this.productLoadingByOrderId[row.id] = true;
    const requestToken = Symbol(`order-products-${row.id}`);
    this.productLoadTokenByOrderId[row.id] = requestToken;
    this.cdr.markForCheck();

    const request = firstValueFrom(this.orderService.getProductsInDoc(row.id))
      .then((data) => {
        if (requestId !== undefined && requestId !== this.lastAppliedSearchRequestId) {
          return;
        }
        if (this.destroyed || this.productLoadTokenByOrderId[row.id] !== requestToken) {
          return;
        }

        const currentRow = this.dataSource.find((item) => item.id === row.id) || row;
        currentRow.products = this.prepareProductRows(data, row.id);
        this.loadProductImages();
      })
      .catch((err) => {
        if (this.destroyed || this.productLoadTokenByOrderId[row.id] !== requestToken) {
          return;
        }

        console.error(err);
        this.notificationService.warning(
          extractApiErrorMessage(err, 'Ошибка загрузки позиций заказа')
        );
      })
      .finally(() => {
        if (this.productLoadTokenByOrderId[row.id] === requestToken) {
          delete this.productLoadTokenByOrderId[row.id];
          delete this.productLoadPromiseByOrderId[row.id];
          delete this.productLoadingByOrderId[row.id];
        }
        this.cdr.markForCheck();
      });

    this.productLoadPromiseByOrderId[row.id] = request;
    return request;
  }

  private loadSupplyForOrder(orderId: number, force = false) {
    if (!force && this.supplyByOrderId[orderId]) {
      return Promise.resolve(this.supplyByOrderId[orderId]);
    }

    const existingRequest = this.supplyLoadPromiseByOrderId[orderId];
    if (existingRequest && !force) {
      return existingRequest;
    }

    const requestToken = Symbol(`order-supply-${orderId}`);
    this.supplyLoadTokenByOrderId[orderId] = requestToken;
    this.supplyLoadingByOrderId[orderId] = true;
    delete this.supplyErrorByOrderId[orderId];
    const request = firstValueFrom(this.orderService.getSupply(orderId))
      .then((supply) => {
        if (this.destroyed || this.supplyLoadTokenByOrderId[orderId] !== requestToken) return undefined;
        this.supplyByOrderId[orderId] = supply;
        const row = this.dataSource.find((item) => item.id === orderId);
        if (row) {
          if (supply.readiness) row.readiness = supply.readiness;
          row.deliveryDeadline = orderDeliveryHint(row);
          row.products = row.products.map((product) => ({
            ...product,
            delivery: productDeliveryHint(product, supply)
          }));
        }
        return supply;
      })
      .catch((error) => {
        if (this.destroyed || this.supplyLoadTokenByOrderId[orderId] !== requestToken) return undefined;
        this.supplyErrorByOrderId[orderId] = true;
        console.error(error);
        return undefined;
      })
      .finally(() => {
        if (this.supplyLoadTokenByOrderId[orderId] === requestToken) {
          delete this.supplyLoadTokenByOrderId[orderId];
          delete this.supplyLoadPromiseByOrderId[orderId];
          delete this.supplyLoadingByOrderId[orderId];
        }
        this.cdr.markForCheck();
      });

    this.supplyLoadPromiseByOrderId[orderId] = request;
    return request;
  }

  private getPlannedSupplyAllocations(
    orderId: number,
    supply = this.supplyByOrderId[orderId]
  ): OrderSupplyAllocation[] {
    return (supply?.lines || []).reduce(
      (result, line) => [
        ...result,
        ...line.allocations.filter((allocation) => allocation.status === 'PLANNED')
      ],
      [] as OrderSupplyAllocation[]
    );
  }

  private handleOrderRefreshPayload(orders: OrderLight[]) {
    let changed = false;
    orders.forEach((order) => {
      const rowIndex = this.dataSource.findIndex((row) => row.id === order.id);
      if (rowIndex < 0) {
        return;
      }

      const previousRow = this.dataSource[rowIndex];
      this.invalidateOrderDetails(order.id);
      const preparedRow = this.prepareOrderRow({
        ...order,
        date: order.date instanceof Date ? order.date : new Date(order.date),
        date_expected:
          order.date_expected instanceof Date ? order.date_expected : new Date(order.date_expected),
        date_canceled: order.date_canceled ? new Date(order.date_canceled) : null
      } as OrderLight);
      if (this.expandedElement?.id === order.id && previousRow.products.length && !preparedRow.products.length) {
        preparedRow.products = previousRow.products;
      }
      this.dataSource[rowIndex] = preparedRow;
      changed = true;

      if (this.expandedElement?.id === order.id) {
        this.expandedElement = preparedRow;
        void this.loadProductsForRow(preparedRow, { force: true });
        void this.loadSupplyForOrder(order.id, true);
      }
    });

    if (changed) {
      this.dataSource = [...this.dataSource];
      this.cdr.markForCheck();
    }
  }

  private invalidateOrderDetails(id: number) {
    // Invalidate collapsed details too; older in-flight reads must not restore them.
    delete this.orderCacheById[id];
    delete this.supplyByOrderId[id];
    delete this.supplyLoadingByOrderId[id];
    delete this.supplyErrorByOrderId[id];
    this.supplyRefreshVersionByOrderId[id] = (this.supplyRefreshVersionByOrderId[id] || 0) + 1;
    delete this.productLoadTokenByOrderId[id];
    delete this.productLoadPromiseByOrderId[id];
    delete this.productLoadingByOrderId[id];
    delete this.supplyLoadTokenByOrderId[id];
    delete this.supplyLoadPromiseByOrderId[id];
  }

  private refreshVisibleOrder(orderId: number) {
    const id = Number(orderId);
    if (this.destroyed || !Number.isInteger(id) || id <= 0) return;
    // During a page request its future visible IDs are not yet known.
    if (!this.loading && !this.loadingMore && !this.dataSource.some((row) => row.id === id)) return;
    this.pendingOrderRefreshIds.add(id);
    this.scheduleLiveRefresh();
  }

  private scheduleLiveRefresh() {
    if (this.destroyed || this.loading || this.loadingMore || this.liveRefreshRunning || this.liveRefreshTimer ||
      !this.pendingOrderRefreshIds.size) return;
    this.liveRefreshTimer = setTimeout(() => {
      this.liveRefreshTimer = null;
      void this.flushLiveRefresh();
    }, 150);
  }

  private async flushLiveRefresh() {
    if (this.destroyed || this.loading || this.loadingMore || this.liveRefreshRunning) return;
    const visibleIds = new Set(this.dataSource.map((row) => row.id));
    const ids = [...this.pendingOrderRefreshIds].filter((id) => visibleIds.has(id));
    this.pendingOrderRefreshIds.clear();
    const pageRequestId = this.searchRequestId;
    this.liveRefreshRunning = true;
    try {
      // Bound reconnect traffic. Row requests never participate in page-search sequencing.
      for (let offset = 0; offset < ids.length; offset += 4) {
        if (this.destroyed) break;
        await Promise.all(ids.slice(offset, offset + 4).map(async (id) => {
          if (pageRequestId !== this.searchRequestId || this.loading || this.loadingMore) {
            this.pendingOrderRefreshIds.add(id);
            return;
          }
          try {
            const res = await firstValueFrom(this.orderService.search({
              id, limit: 1, offset: 0,
              ...(this.appliedSearchPayload?.is_canceled !== undefined
                ? { is_canceled: this.appliedSearchPayload.is_canceled } : {}),
              ...(this.isClientContext ? { client_id: this.scopedClientId } : {})
            }));
            if (this.destroyed) return;
            if (pageRequestId !== this.searchRequestId || this.loading || this.loadingMore) {
              this.pendingOrderRefreshIds.add(id);
              return;
            }
            // Another event arrived after the request started. Fetch once more, not stale data.
            if (this.pendingOrderRefreshIds.has(id)) return;
            const order = res.data.find((row) => row.id === id);
            if (order) {
              this.handleOrderRefreshPayload([order]);
            } else {
              this.dataSource = this.dataSource.filter((row) => row.id !== id);
              this.resultsLength = Math.max(0, this.resultsLength - 1);
              this.nextOffset = Math.max(0, this.nextOffset - 1);
              // A concurrent deletion may shift an unseen last row before the
              // batch cursor. Recheck the tail once, even after an empty batch.
              if (this.appliedSearchPayload) this.hasMoreOrders = true;
              if (this.expandedElement?.id === id) this.expandedElement = null;
              this.invalidateOrderDetails(id);
              this.cdr.markForCheck();
            }
          } catch (error) {
            console.error(error);
          }
        }));
      }
    } finally {
      this.liveRefreshRunning = false;
      this.scheduleLiveRefresh();
      this.scheduleLoadMoreCheck();
    }
  }

  private scheduleRefresh(resetPage = false) {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      this.search(resetPage);
    }, 150);
  }
}
